import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPhone, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../contexts/DataContext';

const MobileDrawer = ({ isOpen, onClose }) => {
  const [isTherapyOpen, setIsTherapyOpen] = useState(false);

  // Get data from global context
  const { therapies, settings } = useData();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    // Therapy is handled with accordion
    { name: 'Services', path: '/services' },
    { name: 'Courses', path: '/courses' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Videos', path: '/videos' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[50] xl:hidden" />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="fixed right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white z-[70] shadow-2xl xl:hidden flex flex-col">
            <div className="p-6 bg-emerald-900 text-white flex justify-between items-center">
              <div>
                <div className="text-xl font-bold">JIVA NADI</div>
                <div className="text-[10px] text-emerald-300 uppercase tracking-widest">Therapy & Chiropractor</div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-3">
              <nav className="space-y-1">
                {navLinks.map((link, idx) => (
                  <React.Fragment key={link.name}>
                    {idx === 2 && (
                      <div className="space-y-1">
                        <button onClick={() => setIsTherapyOpen(!isTherapyOpen)} className="w-full flex items-center justify-between p-4 text-gray-700 font-semibold hover:bg-emerald-50 hover:text-emerald-700 rounded-xl transition-all">
                          <span>Therapy</span>
                          <SafeIcon icon={isTherapyOpen ? FiChevronDown : FiChevronRight} className="w-4 h-4" />
                        </button>
                        <AnimatePresence>
                          {isTherapyOpen && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden bg-gray-50 rounded-xl ml-2">
                              {therapies.map((t) => (
                                <Link key={t.id} to={t.path} onClick={onClose} className="flex items-center space-x-3 p-3 text-sm text-gray-600 hover:text-emerald-700 hover:bg-white rounded-lg transition-all">
                                  <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
                                    <img 
                                      src={t.image} 
                                      alt={t.title}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=100&h=100&fit=crop';
                                      }}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{t.title}</div>
                                    {t.tagline && (
                                      <div className="text-xs text-gray-500 leading-tight line-clamp-2">{t.tagline}</div>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                    <Link to={link.path} onClick={onClose} className="flex items-center justify-between p-4 text-gray-700 font-semibold hover:bg-emerald-50 hover:text-emerald-700 rounded-xl transition-all">
                      {link.name}
                    </Link>
                  </React.Fragment>
                ))}
              </nav>
            </div>

            <div className="p-6 bg-emerald-50">
              <a href={`tel:${settings.contact_phone || settings.phone1 || '9581556660'}`} className="flex items-center justify-center space-x-2 bg-emerald-600 text-white py-4 rounded-2xl font-bold shadow-lg">
                <SafeIcon icon={FiPhone} />
                <span>Call: {settings.contact_phone || settings.phone1 || '9581556660'}</span>
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileDrawer;